'use client';

import '@aejkatappaja/phantom-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryState } from 'nuqs';
import { Link, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  Building2,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Lock,
  LogIn,
  Search,
  Settings,
  Share2,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { fetchAllBusinessDirectory, useBusinessDirectory } from '@/hooks/use-business-directory';
import { useBusinessDirectoryAccess } from '@/hooks/use-business-directory-access';
import { useBusinessDirectoryFacets } from '@/hooks/use-business-directory-facets';
import { useUser } from '@/hooks/use-user';
import { useTenant } from '@/providers/tenant-context';
import {
  directoryAccessFromApiReason,
  type DirectoryAccessKind,
} from '@/lib/business-directory-access-ui';
import { downloadBusinessDirectoryPdf } from '@/lib/business-directory-card-pdf';
import { downloadBusinessDirectoryExcel } from '@/lib/business-directory-excel';
import { BusinessDirectoryFiltersBar } from '@/pages/business/business-directory-filters';
import {
  BusinessDirectoryCard,
  BusinessDirectoryCardPlaceholder,
} from '@/pages/business/business-directory-card';
import { hasActiveDirectoryFilters } from '@/lib/business-directory-params';
import {
  getBusinessDirectoryShareUrl,
  getCommunityRouteSegment,
} from '@/lib/business-directory-url';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CommunityMyBusinessLinks } from '@/components/community/community-my-business-links';
import { AdvertisementBannerCarousel } from '@/components/advertisements/AdvertisementBannerCarousel';
import type {
  AdvertisementCampaign,
  BusinessDirectoryFiltersState,
  BusinessDirectorySort,
  Community,
} from '@/types';

interface BusinessDirectoryViewProps {
  community: Community;
}

const INITIAL_SKELETON_COUNT = 6;
const DEFAULT_DIRECTORY_TITLE = 'Business Directory';

function DirectoryProtectedPage({
  community,
  directoryTitle,
  access,
  loginHref,
  inviteHref,
}: {
  community: Community;
  directoryTitle: string;
  access: Exclude<DirectoryAccessKind, 'allowed' | 'loading'>;
  loginHref: string;
  inviteHref: string;
}) {
  const copy =
    access === 'sign_in'
      ? {
          title: 'Members-only directory',
          body: `${community.name}'s business directory is not public. Sign in with an account that belongs to this community to browse listings.`,
          primary: { label: 'Sign in', href: loginHref, icon: LogIn },
        }
      : access === 'pending_membership'
        ? {
            title: 'Membership pending',
            body: `Your request to join ${community.name} is pending approval. You'll be able to browse the business directory once you're a member.`,
            primary: {
              label: 'View membership status',
              href: `/${getCommunityRouteSegment(community)}/dashboard`,
              icon: UserPlus,
            },
          }
        : access === 'accept_invite'
          ? {
              title: 'Invitation required',
              body: `This business directory is for ${community.name} members only. Accept your community invitation to get access.`,
              primary: { label: 'View invitation', href: inviteHref, icon: UserPlus },
            }
          : access === 'disabled'
            ? {
                title: 'Directory unavailable',
                body: 'The business directory is not enabled for this community.',
                primary: {
                  label: 'Go to dashboard',
                  href: `/${getCommunityRouteSegment(community)}/dashboard`,
                  icon: UserPlus,
                },
              }
            : {
                title: 'Members-only directory',
                body: `This business directory is not shared publicly. Join ${community.name} as a member to view and list businesses.`,
                primary: { label: 'Join community', href: inviteHref, icon: UserPlus },
              };

  const PrimaryIcon = copy.primary.icon;

  return (
    <div className="space-y-8 max-w-xl mx-auto py-8">
      <div className="text-center space-y-3">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted border border-border">
          <Lock className="h-7 w-7 text-muted-foreground" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight">{directoryTitle}</h2>
        {access !== 'disabled' && (
          <p className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-800 dark:text-amber-200">
            Not a public link — members only
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4 text-center shadow-sm">
        <h3 className="text-lg font-semibold">{copy.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
        <Button asChild className="gap-2">
          <Link to={copy.primary.href}>
            <PrimaryIcon className="h-4 w-4" />
            {copy.primary.label}
          </Link>
        </Button>
        {access === 'sign_in' && (
          <p className="text-xs text-muted-foreground">
            Already a member?{' '}
            <Link to={loginHref} className="text-primary font-medium hover:underline">
              Sign in
            </Link>{' '}
            with the email or phone linked to your membership.
          </p>
        )}
      </div>
    </div>
  );
}

export function BusinessDirectoryView({ community }: BusinessDirectoryViewProps) {
  const communityId = community.id;
  const routeSegment = getCommunityRouteSegment(community);
  const directoryTitle = community.businessDirectoryTitle?.trim() || DEFAULT_DIRECTORY_TITLE;
  const shareUrl = getBusinessDirectoryShareUrl(community);
  const loginHref = `/login?redirect=${encodeURIComponent(shareUrl)}`;
  const inviteHref = community.inviteLink
    ? `/invite/${community.inviteLink}`
    : `/${routeSegment}/dashboard`;

  const navigate = useNavigate();
  const { user, communities } = useUser();
  const { tenant } = useTenant();
  const { access: directoryAccessInfo, isLoading: isAccessLoading } =
    useBusinessDirectoryAccess(communityId);

  const communityMember = user ? communities.find((c) => c.id === communityId) : undefined;
  const isJoinedMember = communityMember?.status === 'JOINED';
  const isAdmin = Boolean(
    communityMember?.roles?.some((role) =>
      ['ADMIN', 'CREATOR', 'OWNER'].includes(role.toUpperCase())
    )
  );

  const directoryAccessKind = useMemo((): DirectoryAccessKind => {
    if (isAccessLoading || !directoryAccessInfo) return 'loading';
    return directoryAccessFromApiReason(directoryAccessInfo.reason, directoryAccessInfo.canView);
  }, [directoryAccessInfo, isAccessLoading]);

  const isPublicDirectory = directoryAccessInfo?.directoryPublic === true;
  const canViewDirectory = directoryAccessKind === 'allowed';

  const { data: adsData } = useSWR(
    community.id && community.isAdvertisementEnabled && canViewDirectory
      ? `/api/communities/${community.id}/advertisement-campaigns/active?type=DASHBOARD`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const activeCampaigns: AdvertisementCampaign[] = adsData?.campaigns ?? [];

  const [searchQuery, setSearchQuery] = useQueryState('q', {
    defaultValue: '',
    shallow: true,
    clearOnDefault: true,
  });
  const [category, setCategory] = useQueryState('category', {
    shallow: true,
    clearOnDefault: true,
  });
  const [city, setCity] = useQueryState('city', {
    shallow: true,
    clearOnDefault: true,
  });
  const [state, setState] = useQueryState('state', {
    shallow: true,
    clearOnDefault: true,
  });
  const [country, setCountry] = useQueryState('country', {
    shallow: true,
    clearOnDefault: true,
  });
  const [sort, setSort] = useQueryState('sort', {
    defaultValue: 'business',
    shallow: true,
    clearOnDefault: true,
  });

  const directorySort: BusinessDirectorySort = sort === 'name' ? 'name' : 'business';

  const filters = useMemo<BusinessDirectoryFiltersState>(
    () => ({
      category,
      city,
      state,
      country,
    }),
    [category, city, state, country]
  );

  const handleFiltersChange = (next: BusinessDirectoryFiltersState) => {
    void setCategory(next.category || null);
    void setCity(next.city || null);
    void setState(next.state || null);
    void setCountry(next.country || null);
  };

  const [debouncedSearch] = useDebounce(searchQuery || '', 400);
  const { facets, isLoading: facetsLoading } = useBusinessDirectoryFacets(
    canViewDirectory ? communityId : undefined,
    debouncedSearch,
    filters
  );
  const unfilteredFilters = useMemo<BusinessDirectoryFiltersState>(
    () => ({
      category: null,
      city: null,
      state: null,
      country: null,
    }),
    []
  );
  const { facets: initialFacets } = useBusinessDirectoryFacets(
    canViewDirectory ? communityId : undefined,
    debouncedSearch,
    unfilteredFilters
  );
  const {
    businesses,
    isInitialLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    loadMore,
    metadata,
    isError,
  } = useBusinessDirectory(
    canViewDirectory ? communityId : undefined,
    debouncedSearch,
    filters,
    directorySort
  );

  const loaderRef = useRef<HTMLDivElement>(null);
  const showSkeleton = isInitialLoading || isRefreshing;
  const apiBlockedAccess = useMemo((): Exclude<
    DirectoryAccessKind,
    'allowed' | 'loading'
  > | null => {
    if (!isError || !axios.isAxiosError(isError)) return null;
    const status = isError.response?.status;
    if (status === 401) return 'sign_in';
    if (status === 403) {
      if (!user) return 'sign_in';
      if (communityMember?.status === 'PENDING') return 'pending_membership';
      if (communityMember?.status === 'INVITED') return 'accept_invite';
      return 'join_community';
    }
    return null;
  }, [isError, user, communityMember?.status]);

  const blockedAccess: Exclude<DirectoryAccessKind, 'allowed' | 'loading'> | null =
    apiBlockedAccess ??
    (directoryAccessKind !== 'allowed' && directoryAccessKind !== 'loading'
      ? directoryAccessKind
      : null);

  const showEmptyState =
    canViewDirectory && !blockedAccess && !isError && !showSkeleton && businesses.length === 0;

  const hasUserBusiness = Boolean(user?.businesses && user.businesses.length > 0);

  const showDirectoryListingsManager = Boolean(
    user && isJoinedMember && community.controlledBusinessListing
  );

  const listingsPanelStorageKey = `milora:directory-listings-panel:${communityId}`;
  const [showListingsPanel, setShowListingsPanel] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(listingsPanelStorageKey) === 'true';
  });

  useEffect(() => {
    localStorage.setItem(listingsPanelStorageKey, String(showListingsPanel));
  }, [showListingsPanel, listingsPanelStorageKey]);

  const showAddBusinessCta = Boolean(
    community.businessDirectoryShowAddBusiness &&
    !showDirectoryListingsManager &&
    (!user || !isJoinedMember || !hasUserBusiness)
  );

  const addBusinessHref = useMemo(() => {
    if (!user) return loginHref;
    if (!isJoinedMember) return inviteHref;
    return `/${communityId}/create/business`;
  }, [communityId, inviteHref, isJoinedMember, loginHref, user]);

  const addBusinessLabel = !user
    ? 'Sign in to add your business'
    : !isJoinedMember
      ? 'Join the community to add your business'
      : 'Add your business';

  const directoryCountLabel = useMemo(() => {
    if (!metadata || metadata.totalCount <= 0) return null;

    const count = metadata.totalCount;
    const noun = count === 1 ? 'business' : 'businesses';
    const hasQuery = Boolean(debouncedSearch.trim()) || hasActiveDirectoryFilters(filters);

    if (hasQuery) {
      return `${count} ${noun} match your search`;
    }

    return `${count} ${noun}`;
  }, [metadata, debouncedSearch, filters]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const node = loaderRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [loadMore]);

  const [isExporting, setIsExporting] = useState(false);

  const handleSharePage = async () => {
    const shareData: ShareData = {
      title: directoryTitle,
      text: `Browse businesses in ${community.name} on Milora.`,
      url: shareUrl,
    };

    try {
      const canNativeShare =
        typeof navigator.share === 'function' &&
        (typeof navigator.canShare !== 'function' || navigator.canShare(shareData));

      if (canNativeShare) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      } catch {
        toast.error('Failed to share');
      }
    }
  };

  const handleExportDirectory = async (format: 'pdf' | 'excel') => {
    if (isExporting || businesses.length === 0) return;
    setIsExporting(true);
    try {
      const exportBusinesses = await fetchAllBusinessDirectory(
        communityId,
        debouncedSearch,
        filters,
        directorySort
      );
      const listings = exportBusinesses.length > 0 ? exportBusinesses : businesses;

      if (format === 'excel') {
        downloadBusinessDirectoryExcel(listings, community.name, directorySort);
        toast.success('Directory Excel downloaded');
      } else {
        await downloadBusinessDirectoryPdf(
          listings,
          community.name,
          community.logo,
          directorySort,
          community.themeColors?.primary || tenant?.themeColors?.primary
        );
        toast.success('Directory PDF downloaded');
      }
    } catch {
      toast.error(format === 'excel' ? 'Failed to download Excel' : 'Failed to download PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (directoryAccessKind === 'loading') {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
      </div>
    );
  }

  if (blockedAccess) {
    return (
      <DirectoryProtectedPage
        community={community}
        directoryTitle={directoryTitle}
        access={blockedAccess}
        loginHref={loginHref}
        inviteHref={inviteHref}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Advertisement Banner */}
      {community.isAdvertisementEnabled && activeCampaigns.length > 0 && (
        <AdvertisementBannerCarousel communityId={community.id} campaigns={activeCampaigns} />
      )}

      <div className="relative pr-12">
        <div className="absolute right-0 top-0 flex flex-col gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => void handleSharePage()}
            aria-label="Share business directory"
          >
            <Share2 className="h-5 w-5" />
          </Button>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  disabled={isInitialLoading || isExporting || businesses.length === 0}
                  aria-label="Download business directory"
                  title="Download directory"
                >
                  {isExporting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Download className="h-5 w-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  disabled={isExporting}
                  onSelect={() => void handleExportDirectory('pdf')}
                >
                  <FileText className="h-4 w-4" />
                  Download as PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  disabled={isExporting}
                  onSelect={() => void handleExportDirectory('excel')}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Download as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {showDirectoryListingsManager && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`h-10 w-10 shrink-0 ${showListingsPanel ? 'bg-muted text-foreground' : ''}`}
              onClick={() => setShowListingsPanel((open) => !open)}
              aria-label={
                showListingsPanel ? 'Hide your directory listings' : 'Show your directory listings'
              }
              aria-pressed={showListingsPanel}
            >
              <Settings className="h-5 w-5" />
            </Button>
          )}
        </div>
        <div>
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
            {directoryTitle}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Discover and connect with businesses.
          </p>
          {!isPublicDirectory && isJoinedMember && (
            <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-0.5">
              <Lock className="h-3 w-3" />
              Members only — not a public link
            </p>
          )}
        </div>
      </div>

      {showDirectoryListingsManager && showListingsPanel && (
        <CommunityMyBusinessLinks
          community={community}
          title="Manage your directory listings"
          description="Add, remove, or track approval for businesses you want visible in this directory."
        />
      )}

      {showAddBusinessCta && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-primary shrink-0" />
            <p className="text-sm font-medium">{addBusinessLabel}</p>
          </div>
          <Button asChild size="sm" className="shrink-0">
            <Link to={addBusinessHref}>
              {!user ? 'Sign in' : !isJoinedMember ? 'Join' : 'Add business'}
            </Link>
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <div className="relative group max-w-2xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
          <Input
            placeholder="Search by name, category, address, owner or email..."
            value={searchQuery || ''}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-9 bg-white backdrop-blur-sm border-muted-foreground/20 focus-visible:ring-primary/20 transition-all shadow-sm"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery(null)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <BusinessDirectoryFiltersBar
          facets={facets}
          initialFacets={initialFacets}
          filters={filters}
          sort={directorySort}
          isLoading={facetsLoading}
          onChange={handleFiltersChange}
          onSortChange={(next) => void setSort(next)}
        />

        {directoryCountLabel && !isInitialLoading && (
          <p className="text-xs text-muted-foreground leading-none">{directoryCountLabel}</p>
        )}

        {isInitialLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
            {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
              <phantom-ui key={index} loading animation="shimmer" reveal={0.2} class="block h-full">
                <BusinessDirectoryCardPlaceholder />
              </phantom-ui>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
            {businesses.map((business) => (
              <phantom-ui
                key={business.id}
                loading={isRefreshing || undefined}
                animation="shimmer"
                reveal={0.2}
                class="block h-full"
              >
                <BusinessDirectoryCard
                  business={business}
                  sort={directorySort}
                  onNavigate={(businessId) => navigate(`/${routeSegment}/b/${businessId}`)}
                />
              </phantom-ui>
            ))}
          </div>
        )}

        {showEmptyState && (
          <div className="text-center py-12 flex flex-col items-center border-2 border-dashed rounded-2xl bg-muted/5 border-muted-foreground/10">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <Search className="h-6 w-6 text-muted-foreground opacity-40" />
            </div>
            <p className="text-base font-semibold text-foreground">No businesses found</p>
            <p className="text-sm text-muted-foreground max-w-xs mt-1">
              {searchQuery || hasActiveDirectoryFilters(filters)
                ? 'No businesses match your current search or filters. Try adjusting them.'
                : 'There are currently no businesses registered in this community.'}
            </p>
            {(searchQuery || hasActiveDirectoryFilters(filters)) && (
              <Button
                variant="link"
                onClick={() => {
                  void setSearchQuery(null);
                  handleFiltersChange({
                    category: null,
                    city: null,
                    state: null,
                    country: null,
                  });
                }}
                className="mt-1 text-primary hover:no-underline"
              >
                Clear search and filters
              </Button>
            )}
          </div>
        )}

        {hasMore && !showSkeleton && (
          <div ref={loaderRef} className="flex justify-center py-4">
            {isLoadingMore && <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />}
          </div>
        )}
      </div>
    </div>
  );
}
